import { AuthError, PostgrestError } from "@supabase/supabase-js";
import { Capacitor } from "@capacitor/core";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import logSentryError from "utils/logSentryError";

class AuthApi {
  static DEEP_LINK = "com.groupup.live.app://loginWithGoogle";

  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  static async setUserSession(hashParams: URLSearchParams) {
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    const expiresIn = hashParams.get("expires_in");
    const tokenType = hashParams.get("token_type");

    if (accessToken && refreshToken && expiresIn && tokenType) {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        logSentryError(error);
      }
    }
  }

  async refreshSession() {
    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      logSentryError(error, "AUTH_API: signIn");
    }

    return data;
  }

  /**
   * Sign in user
   *
   * @param email
   * @param password
   * @returns
   */
  async signIn(email: string, password: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: error.message,
          severity: "error",
        });
      }

      if (error.status !== 400) {
        logSentryError(error, "AUTH_API: signIn");
      }
      return { error };
    }

    return { error: null };
  }

  async signInWithGoogle(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: Capacitor.isNativePlatform() ? AuthApi.DEEP_LINK : undefined,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: error.message,
          severity: "error",
        });
      }

      logSentryError(error, "AUTH_API: signInWithGoogle");
      return { error };
    }

    return { error: null };
  }

  /**
   * Signs up new user
   *
   * @param email
   * @param password
   * @returns [true] if sign up was successful, [false] otherwise
   */
  async signUp(email: string, password: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signUp({ email, password });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: error.message,
          severity: "error",
        });
      }

      logSentryError(error, "AUTH_API: signUp");
      return { error };
    }

    if (this.showNotification) {
      this.showNotification({
        message: "Account created successfully. Verify your email address by checking your inbox.",
        severity: "success",
      });
    }

    return { error: null };
  }

  /**
   * Set the [profileCreated] metadata prop of the authenticated user object to [true]
   *
   * @returns
   */
  async addProfileCreatedMeta(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({
      data: { profileCreated: true },
    });

    if (error) {
      logSentryError(error, "AUTH_API: addProfileCreatedMeta");
      return { error };
    }

    return { error: null };
  }

  /**
   * Send reset password email to user
   *
   * @param email
   * @returns
   */
  async sendResetPasswordEmail(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.resetPasswordForEmail(email);

    if (error) {
      if (this.showNotification) {
        this.showNotification({ message: error.message, severity: "error" });
      }

      logSentryError(error, "AUTH_API: sendResetPasswordEmail");
      return { error };
    }

    if (this.showNotification) {
      this.showNotification({
        message: "Check you email to successfully reset your password.",
        severity: "success",
      });
    }

    return { error: null };
  }

  /**
   * Set new password
   *
   * @param newPassword
   * @returns
   */
  async resetPassword(newPassword: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to reset password.",
          severity: "error",
        });
      }

      logSentryError(error, "AUTH_API: resetPassword");
      return { error };
    }

    if (this.showNotification) {
      this.showNotification({
        message: "Password successfully changed.",
        severity: "success",
      });
    }

    return { error: null };
  }

  /**
   * Update user email
   *
   * @param newEmail
   * @returns
   */
  async updateEmail(newEmail: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({ email: newEmail });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: error.message,
          severity: "error",
        });
      }

      logSentryError(error, "AUTH_API: updateEmail");
      return { error };
    }

    if (this.showNotification) {
      this.showNotification({
        message: "To complete the email change process, please check both your old and new email inboxes.",
        severity: "success",
      });
    }

    return { error: null };
  }

  /**
   * Update user password
   *
   * @param newPassword
   * @returns
   */
  async updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to change password.",
          severity: "error",
        });
      }

      logSentryError(error, "AUTH_API: updatePassword");
      return { error };
    }

    if (this.showNotification) {
      this.showNotification({
        message: "Successfully changed account password.",
        severity: "success",
      });
    }

    return { error: null };
  }

  /**
   * Delete user account
   *
   * @returns
   */
  async deleteAccount(): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.rpc("delete_user");

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to delete account.",
          severity: "error",
        });
      }

      logSentryError(error, "AUTH_API: deleteAccount");
      return { error };
    }
    if (this.showNotification) {
      this.showNotification({
        message: "Account successfully deleted.",
        severity: "success",
      });
    }

    return { error: null };
  }

  /**
   * Signs out user
   */
  async signOut(): Promise<{ error: AuthError | null }> {
    const { error } = await supabase.auth.signOut();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to sign out.",
          severity: "error",
        });
      }

      logSentryError(error, "AUTH_API: signOut");
      return { error };
    }

    return { error: null };
  }
}

export default AuthApi;
